import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './CaseModal.module.scss';
import Modal from '@/components/Modal/Modal';
import { TelegramSticker } from '@/components/TelegramSticker';
import WebApp from '@twa-dev/sdk';
import CodeLock from './CodeLock';
import {
  checkSubscription,
  openCase,
  getLeaderboard,
  getAttemptsInfo,
  getSafeTasks,
  checkConditions,
} from '@/api/modules/Case/case';
import confetti from 'canvas-confetti';
import { useTickSound } from '@/hooks/UseTickSound';
import useAttemptsStore from '@/store/AttemptsStore';
import { useNotificationStore } from '@/store/NotificationStore';
import { TimeDisplay } from '@/utils/TimeDisplay';
import CaseTaskItem from './CaseTaskItem';
import { Loader } from '@/components/Loader/Loader';

interface RaffleModalProps {
  open: boolean;
  onClose: () => void;
  isSubscribed: boolean;
  setIsSubscribed: (isSubscribed: boolean) => void;
  caseData: any;
  onLeaderboardUpdate?: () => void;
}
interface AttemptsInfo {
  code_attempt: string;
  correct_count: number;
  created_at: string;
  date: string;
  id: number;
  is_opened: boolean;
  save_event_id: string;
}

interface AttemptsResponse {
  attempts_today: AttemptsInfo[];
}

const CaseModal: React.FC<RaffleModalProps> = ({
  open,
  onClose,
  isSubscribed,
  setIsSubscribed,
  caseData,
  onLeaderboardUpdate,
}) => {
  if (!open) return null;
  const tg = window?.Telegram?.WebApp;

  const [clicked, setClicked] = useState(false);
  const [code, setCode] = useState('000');
  const [matches, setMatches] = useState<number | null>(null);
  const [guessed, setGuessed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [blockedTime, setBlockedTime] = useState('');
  const [isProcessingCase, setIsProcessingCase] = useState(false);
  const [isWheelDisabled, setIsWheelDisabled] = useState(false);
  const attempts = useAttemptsStore((s) => s.attempts);
  const setAttempts = useAttemptsStore((s) => s.setAttempts);
  const blocked = useAttemptsStore((s) => s.blocked);
  const setBlocked = useAttemptsStore((s) => s.setBlocked);
  const setNotification = useNotificationStore((s) => s.setNotification);
  const shouldDisableClose = isProcessingCase;
  // Мгновенная блокировка от двойных нажатий до обновления state
  const openCaseLockRef = useRef(false);
  const [attemptsInfo, setAttemptsInfo] = useState<AttemptsInfo[]>([]);
  const [safeTasks, setSafeTasks] = useState<any[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isTasksCompleted, setIsTasksCompleted] = useState(
    caseData?.user?.is_done !== false
  );

  const fetchAttemptsInfo = async () => {
    try {
      const response: AttemptsResponse = await getAttemptsInfo(
        caseData.safe_event_id,
      );
      console.log(response, 'ATTEMPTS INFO');
      setAttemptsInfo(response.attempts_today || []);
    } catch (error) {
      console.error('Error fetching attempts info:', error);
      setAttemptsInfo([]);
    }
  };

  useEffect(() => {
    fetchAttemptsInfo();
  }, [caseData.safe_event_id]);

  // Обновляем локальное состояние при изменении caseData
  useEffect(() => {
    setIsTasksCompleted(caseData?.user?.is_done !== false);
  }, [caseData?.user?.is_done]);

  const fetchSafeTasks = useCallback(async () => {
    if (caseData?.user?.is_done === false && !isTasksCompleted) {
      setIsLoadingTasks(true);
      try {
        const response = await getSafeTasks();
        console.log(response, 'SAFE TASKS');
        if (response?.status === 'success' && response?.tasks) {
          setSafeTasks(response.tasks);
        }
      } catch (error) {
        console.error('Error fetching safe tasks:', error);
        setSafeTasks([]);
      } finally {
        setIsLoadingTasks(false);
      }
    } else {
      setSafeTasks([]);
      setIsLoadingTasks(false);
    }
  }, [caseData?.user?.is_done, isTasksCompleted]);

  useEffect(() => {
    if (open) {
      fetchSafeTasks();
    }
  }, [open, fetchSafeTasks]);

  // Функция для обновления прогресса задачи
  const handleTaskUpdate = useCallback((taskId: number, updatedTask: any) => {
    setSafeTasks((prevTasks) => {
      const updatedTasks = prevTasks.map((task: any) =>
        task.id === taskId ? { ...task, ...updatedTask } : task
      );

      // Проверяем, достиг ли прогресс цели для обновленной задачи
      const updatedTaskData = updatedTasks.find((t: any) => t.id === taskId);
      if (updatedTaskData && updatedTaskData.user_progress === updatedTaskData.target_progress) {
        // Перезагружаем задачи после небольшой задержки, чтобы дать время UI обновиться
        setTimeout(() => {
          fetchSafeTasks();
        }, 500);
      }

      return updatedTasks;
    });
  }, [fetchSafeTasks]);


  // Подсчитываем выполненные задачи
  const completedTasksCount = safeTasks.filter(
    (task: any) => task.status === 'claim'
  ).length;
  const totalTasksCount = safeTasks.length;
  const isAllTasksDone = safeTasks.length > 0 && completedTasksCount === totalTasksCount;
  
  const getAttemptsText = (attemptsCount: number) => {
    const lastDigit = attemptsCount % 10;
    const lastTwoDigits = attemptsCount % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19)
      return `${attemptsCount} попыток`;
    if (lastDigit === 1) return `${attemptsCount} попытку`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${attemptsCount} попытки`;
    return `${attemptsCount} попыток`;
  };

  const handleSubscribe = () => {
    tg?.openTelegramLink?.(caseData.user.subscribed_utils.link);
    setClicked(true);
  };

  const handleCheckSubscription = async () => {
    const res = await checkSubscription(caseData.safe_event_id);
    if (res.subscribed) {
      setIsSubscribed(true);
    } else {
      setClicked(false);
      setNotification({ type: 'Error', message: 'Сперва подпишись на канал' });
    }
  };

  // 🔊 Звук победы
  const { ready, init, playOnce } = useTickSound({
    src: '/opening.wav',
    volume: 1,
  });

  const handleOpenCase = async () => {
    if (isLoading) return;
    if (openCaseLockRef.current) return;

    openCaseLockRef.current = true;

    try {
      setIsLoading(true);
      setIsProcessingCase(true);
      setIsWheelDisabled(true);

      let vibrationInterval: NodeJS.Timeout | undefined;
      if ((window?.Telegram?.WebApp as any)?.HapticFeedback) {
        vibrationInterval = setInterval(() => {
          (
            window?.Telegram?.WebApp as any
          ).HapticFeedback?.notificationOccurred('success');
        }, 200);
      }

      await new Promise((r) => setTimeout(r, 2000));
      if (vibrationInterval) clearInterval(vibrationInterval);

      const sentCode = code.split('').map(Number);
      const res = await openCase(caseData.safe_event_id, sentCode);

      if (res.is_correct === false) {
        setAttempts(caseData.max_attempts_per_day - res.attempts_left);
        setMatches(res.match_count);
        // Обновляем информацию о попытках после неуспешной попытки
        await fetchAttemptsInfo();
        if (res.attempts_left === 0) {
          setBlocked(true);
          setBlockedTime(caseData.ends_at);
        }
      } else if (res.is_correct === true) {
        if (!ready) await init();
        setGuessed(true);
        setBlocked(true);
        setBlockedTime(caseData.user.next_open_at);

        try {
          await getLeaderboard(caseData.safe_event_id);
          onLeaderboardUpdate?.();
          // Обновляем информацию о попытках после успешного открытия
          await fetchAttemptsInfo();
        } catch {}

        const defaults = {
          spread: 360,
          ticks: 50,
          gravity: 0,
          decay: 0.94,
          startVelocity: 30,
          colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
          zIndex: 99999,
        };
        const shoot = () => {
          confetti({
            ...defaults,
            particleCount: 80,
            scalar: 1.2,
            shapes: ['star'],
          });
          confetti({
            ...defaults,
            particleCount: 30,
            scalar: 0.75,
            shapes: ['circle'],
          });
        };
        playOnce();
        setTimeout(shoot, 200);
        setTimeout(shoot, 400);
        setTimeout(shoot, 600);
        setTimeout(shoot, 800);
      } else {
        setBlocked(true);
        setBlockedTime(caseData.user.next_open_at);
      }
    } catch (e) {
      setNotification({ type: 'Error', message: 'Ошибка при открытии сейфа' });
    } finally {
      setIsLoading(false);
      setIsProcessingCase(false);
      setIsWheelDisabled(false);
      openCaseLockRef.current = false;
    }
  };

  if (!isSubscribed) {
    return (
      <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
        <div>
          <h2 className={styles.title}>Доступ для участников канала!</h2>
          <p className={styles.descriptionNoSubscribe}>
            Чтобы участвовать в турнире, <br />
            необходимо подписаться на наш <br />
            <span onClick={handleSubscribe} className={styles.channel}>
              официальный канал.
            </span>
          </p>

          {!clicked ? (
            <button className={styles.withdrawButton} onClick={handleSubscribe}>
              Подписаться
            </button>
          ) : (
            <button
              className={styles.withdrawButton}
              onClick={handleCheckSubscription}
            >
              Проверить подписку
            </button>
          )}
        </div>
      </Modal>
    );
  }

  // Экран с заданиями
  if (caseData?.user?.is_done === false && !isTasksCompleted) {
    const handleCheckCompletion = async () => {
      console.log(caseData, "SDS")
        const res = await checkConditions(caseData.safe_event_id);
        console.log(res, 'CHECK CONDITIONS');
        
        if (res.status === "success") {
          // Показываем экран открытия сейфа
          setIsTasksCompleted(true);
          
          // Обновляем флаг is_done в caseData
          if (caseData?.user?.is_done === false) {
            caseData.user.is_done = true;
          }
        }
    };

    return (
      <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
        <div className={styles.tasksScreen}>
          {/* Картинка кейса */}
          <div
            className={styles.caseImage}
            style={{
              backgroundImage: caseData?.details?.image_url
                ? `url(${caseData.details.image_url})`
                : 'url(../../../public/casebg.webp)',
            }}
          />

          <h2 className={styles.titleSafe}>
          Выполни задания, чтобы открыть сейф:
          </h2>

          {isLoadingTasks ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : (
            <>
              {/* Задания */}
              {safeTasks.length > 0 && (
                <div className={styles.tasksContainer}>
                  {safeTasks.map((task: any) => (
                    <CaseTaskItem 
                      key={task.id} 
                      task={task} 
                      safe_event_id={caseData.safe_event_id}
                      onTaskUpdate={handleTaskUpdate}
                    />
                  ))}
                </div>
              )}

              {/* Кнопка */}
              {totalTasksCount > 0 && (
                <button
                  className={`${styles.withdrawButton} ${!isAllTasksDone ? styles.blocked : ''}`}
                  onClick={handleCheckCompletion}
                  disabled={!isAllTasksDone}
                >
                  {isAllTasksDone
                    ? 'Проверить выполнение'
                    : `Выполнено ${completedTasksCount}/${totalTasksCount} заданий`}
                </button>
              )}
            </>
          )}
        </div>
      </Modal>
    );
  }

  if (guessed) {
    return (
      <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
        <div>
          <h2 className={styles.title}>
            <span>Получилось! Сейф взломан.</span>
          </h2>
          <div className={styles.stickerWrapper}>
            <TelegramSticker
              tgsUrl="./Chest.tgs"
              style={{ width: 180, height: 180 }}
              loop={false}
              autoplay
            />
          </div>
          <p className={styles.description}>
            Тебе удалось подобрать правильный код за{' '}
            {getAttemptsText(attempts + 1)}. <br />
            Твой результат записан в таблицу лидеров.
          </p>
          <button className={styles.withdrawButton} onClick={onClose}>
            Продолжить
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
      <div className={styles.caseModal}>
        <h2 className={styles.title}>Подбери правильный код!</h2>

        <div className={styles.codeLock}>
          <CodeLock
            value={code}
            onChange={setCode}
            height={120}
            adPhase={isProcessingCase}
            disabled={isWheelDisabled}
          />
        </div>

        {matches !== null && (
          <p className={styles.matches}>
            Совпадений: {matches}/{code.length}
          </p>
        )}

        {blocked ? (
          <button
            className={`${styles.withdrawButton} ${styles.blocked}`}
            disabled
          >
            <div className={styles.blockedContent}>
              <div className={styles.blockedTimer}>
                Повтор через <TimeDisplay endDate={blockedTime} />
              </div>
            </div>
          </button>
        ) : (
          <button
            className={`${styles.withdrawButton} ${isLoading ? styles.loading : ''}`}
            onClick={handleOpenCase}
            disabled={isLoading}
          >
            {isLoading
              ? 'Открываю...'
              : `Открыть ${attempts}/${caseData.max_attempts_per_day}`}
          </button>
        )}

        {attemptsInfo.length > 0 && (
          <div className={styles.attemptsHistory}>
            <h3 className={styles.attemptsTitle}>Последняя попытка</h3>
            <div className={styles.attemptsList}>
              {attemptsInfo.slice(0, 1).map((attempt, index) => (
                <div key={attempt.id || index} className={styles.attemptItem}>
                  <div className={styles.attemptHeader}>
                    <span className={styles.attemptCode}>
                      {attempt.code_attempt}
                    </span>
                    <span className={styles.attemptDate}>
                      {new Date(attempt.date).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  <div className={styles.attemptDetails}>
                    <div className={styles.attemptStat}>
                      <span className={styles.statLabel}>Совпадений:</span>
                      <span className={styles.statValue}>
                        {attempt.correct_count}/3
                      </span>
                    </div>
                    <div className={styles.attemptStatus}>
                      {attempt.is_opened ? (
                        <span className={styles.statusSuccess}>✓ Открыт</span>
                      ) : (
                        <span className={styles.statusFailed}>✗ Не открыт</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CaseModal;
